import * as THREE from 'three';

export interface ClothOptions {
  width: number; height: number;
  /** `left`: pinned along its left edge and streaming towards +x like a flag. `top`: hanging from its top edge like washing. */
  pin: 'left' | 'top';
  color?: number; material?: THREE.Material;
  /** Ragged hem depth in metres for torn cloth. */
  tatter?: number;
  /** Swallow-tail notch depth as a fraction of the length (flags). */
  tail?: number;
  phase?: number; amplitude?: number;
}

const hash = (x: number, y: number) => { const v = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return v - Math.floor(v); };

/**
 * One shared cloth motion for every flag, pennant and hanging rag: still at the pin, rippling by a travelling
 * wave that grows towards the free end (the same wave as the crossroads banner). The origin is the pin.
 */
export function createCloth(options: ClothOptions) {
  const { width, height, pin, tatter = 0, tail = 0, phase = 0 } = options;
  const geometry = new THREE.PlaneGeometry(width, height, pin === 'left' ? 18 : 6, pin === 'left' ? 4 : 12);
  const length = pin === 'left' ? width : height;
  if (pin === 'left') geometry.translate(width / 2, 0, 0); else geometry.translate(0, -height / 2, 0);
  const positions = geometry.getAttribute('position');
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i), y = positions.getY(i), u = (pin === 'left' ? x : -y) / length;
    if (pin === 'left' && tail) positions.setX(i, x - Math.max(0, (u - (1 - tail * 1.4)) / (tail * 1.4)) * (1 - Math.abs(y) / (height / 2)) * length * tail);
    if (pin === 'left' && !tail) positions.setY(i, y * (1 - u * .35));
    if (pin === 'top' && tatter && u > .7) positions.setY(i, y - hash(Math.round(x * 20), 1) * tatter * (u - .7) / .3);
  }
  const original = Float32Array.from(positions.array);
  const material = options.material ?? new THREE.MeshStandardMaterial({ color: options.color ?? 0xffffff, side: THREE.DoubleSide, roughness: .95 });
  const mesh = new THREE.Mesh(geometry, material); mesh.castShadow = true;
  const swing = options.amplitude ?? Math.min(.8, length * .1);
  geometry.computeBoundingSphere(); geometry.boundingSphere!.radius += swing * 2 + 1;
  const update = (time: number) => {
    for (let i = 0; i < positions.count; i++) {
      const x = original[i * 3], y = original[i * 3 + 1], u = Math.max(0, (pin === 'left' ? x : -y) / length), along = u * length;
      const wave = along * (7 / length) - time * 3.5 + phase;
      positions.setZ(i, (Math.sin(wave) * u + Math.sin(along * (13 / length) - time * 5.2 + phase) * u * .16) * swing);
      if (pin === 'left') positions.setY(i, y + Math.sin(wave * .65) * u * swing * .37);
      else positions.setX(i, x + Math.sin(wave * .5 + 1) * u * u * swing * .3);
    }
    positions.needsUpdate = true; geometry.computeVertexNormals();
  };
  update(0);
  return { mesh, update };
}
